import { useMemo } from 'react';
import { CloseButton, HStack, Span } from '@chakra-ui/react';
import { Box, Flex } from '@chakra-ui/react';
import type { FileMsgInfo, Msg, SessionStoreInstance } from '@halo/chat-sdk-react';
import { FileActionType } from '@halo/chat-sdk';
import { FileTypeIcon } from '@halo/ui-chat-react';
import { formatFileSize } from '@halo/utils';
import { isEqual } from 'lodash-es';
import { MessageUpload } from '../MessageMeta/MessageUpload';

export interface MessageFileProps extends Pick<MsgFileStatusProps, 'onAbort'> {
  message: Msg;
  metaChildren?: React.ReactNode;
  sessionStore?: SessionStoreInstance;
}

export default function MessageFile({ message, sessionStore }: MessageFileProps) {
  const [fileMsg, setFileMsg] = useState<FileMsgInfo>();

  useEffect(() => {
    const unsubscribe = sessionStore?.subscribe(
      (state) => state.fileMsgs[message.cli_msg_id],
      (fileMsg) => {
        setFileMsg(fileMsg);
      },
      {
        fireImmediately: true,
        equalityFn: (a, b) => isEqual(a, b),
      }
    );
    return unsubscribe;
  }, [message.cli_msg_id, sessionStore]);

  const maskRef = useRef<HTMLElement>(null);

  return (
    <Box position="relative" maxW="300px" minW="200px" userSelect="none">
      <HStack gap="1.5" alignItems="center">
        <Box position="relative" flex="none">
          <FileTypeIcon extType={message.payload.ext} width={`44px`} height={`44px`} />
          <MessageUpload
            message={message}
            sessionStore={sessionStore}
            fileMsg={fileMsg}
            boxSize="8"
            fontSize="16px"
            maskRef={maskRef}
          />
        </Box>
        <Box ml="1" minW="0">
          <Box fontWeight="medium" fontSize="sm" w="full" lineClamp={2}>
            {message.payload?.name}
          </Box>
          <Flex mt="1" justify="space-between" align="center" gap="1" whiteSpace="nowrap">
            <Box fontWeight="medium" fontSize="xs" color="fg.muted">
              {formatFileSize(message.payload.size)}
            </Box>
          </Flex>
          {/* <MsgFileStatus
            type={
              message.fileInfo?.progress !== undefined && message.fileInfo?.progress !== 100
                ? message.fileInfo?.type
                : undefined
            }
            size={message.payload.size}
            onAbort={onAbort}
          >
            {metaChildren}
          </MsgFileStatus> */}
        </Box>
      </HStack>
      <Box
        ref={maskRef}
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        cursor="pointer"
      ></Box>
    </Box>
  );
}

export { MessageFile };

export interface MsgFileStatusProps {
  type: FileActionType | undefined;
  size: number;
  showSize?: boolean;
  children?: React.ReactNode;
  onAbort?: () => void;
}

export function MsgFileStatus({
  type,
  size,
  showSize = true,
  onAbort,
  children,
}: MsgFileStatusProps) {
  const sizeDisplay = useMemo(() => formatFileSize(size), [size]);

  return (
    <Flex justify="space-between" align="center" gap="1" whiteSpace="nowrap">
      {showSize && (
        <Box fontWeight="medium" fontSize="xs" color="fg.muted">
          {sizeDisplay}
        </Box>
      )}
      {type ? (
        <Flex justify="space-between" align="center" flexGrow={1} gap="1">
          <Span lineHeight="1" fontSize="xs" color="fg.subtle">
            {type === FileActionType.UPLOAD ? 'Uploading...' : 'Downloading...'}
          </Span>
          <CloseButton size="2xs" onClick={() => onAbort?.()} />
        </Flex>
      ) : (
        children
      )}
    </Flex>
  );
}
